/*
 * "Alert me": when we don't have a card, put it on the customer's want list so we email them when we list one.
 * The want list is Collect's (collect.pwcards.com), the one that already sends alert emails; this is a shortcut into it.
 *
 *   PWWant.search(q)            every English card matching q (from the Collect server, not just what's in stock)
 *   PWWant.open(card)           the sheet: condition, then save (signed in) or sign in first; no price (any copy we list)
 *   [data-pw-want-search]       filled on the search page when the shop has no listing for the search
 *   ?pw_alert=TWM-130           opens the sheet for that card (staff QR codes at shows)
 *   ?pw_alert=TWM-130&pw_cond=NM&pw_save=1   back from signing in / creating an account: saves it straight away
 *
 * Signed out, "Alert me" sends them to create a free account (or sign in) and the card rides along in the return
 * link (pw_save=1), so it's saved wherever sign-in finishes. That matters on phones: Instagram's browser, the Shop
 * app or a code email opened in another browser all lose localStorage. localStorage ("pw-want-pending") is kept as
 * a second copy. Config comes from window.PWC.want (snippets/pwc-head.liquid).
 */
(() => {
  const C = (window.PWC && window.PWC.want) || null;
  if (!C) return;
  const PENDING = 'pw-want-pending';
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = (n) => '\u20ac' + (Number(n) % 1 ? Number(n).toFixed(2) : String(Math.round(Number(n))));
  const toast = (html, ms) => (window.PWC && PWC.toast ? PWC.toast(html, { timeout: ms || 4200 }) : null);
  const store = {
    get: () => { try { const v = JSON.parse(localStorage.getItem(PENDING) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; } },
    set: (v) => { try { localStorage.setItem(PENDING, JSON.stringify(v.slice(-20))); } catch (e) {} },
  };

  const cache = new Map();
  function search(q, n = 6) {
    q = String(q || '').trim();
    if (q.length < 2) return Promise.resolve([]);
    const key = n + '|' + q.toLowerCase();
    if (!cache.has(key)) {
      cache.set(key, fetch(`${C.api}/api/cards?q=${encodeURIComponent(q)}&n=${n}`)
        .then((r) => (r.ok ? r.json() : { cards: [] }))
        .then((d) => d.cards || [])
        .catch(() => { cache.delete(key); return []; }));
    }
    return cache.get(key);
  }

  // card picture from Collect's set sheet (64 cards a sheet, 16 across)
  function art(card, cls = '') {
    const i = card.img;
    if (!i) return `<span class="pww-art pww-art--none ${cls}" aria-hidden="true"><span>${esc(card.name)}</span></span>`;
    const pos = `${(i.col / 15) * 100}% ${i.rows > 1 ? (i.row / (i.rows - 1)) * 100 : 0}%`;
    return `<span class="pww-art ${cls}" role="img" aria-label="${esc(card.name)}" style="background-image:url('${esc(i.u)}');background-size:1600% ${i.rows * 100}%;background-position:${pos}"></span>`;
  }
  const line = (c) => `${esc(c.set)} \u00b7 <span class="pww-mono">${esc(c.n)}${c.off ? '/' + esc(c.off) : ''}</span>`;
  const stockLine = (c) =>
    c.inStock && c.handle
      ? `<a class="pww-in" href="/products/${esc(c.handle)}">In stock from ${money(c.from)}</a>`
      : `<span class="pww-out">Not in stock right now</span>`;

  /* ------------------------------------------------------------------ save */
  function post(w) {
    if (!C.auth) return Promise.reject(new Error('signed out'));
    return fetch(`${C.api}/api/want`, {
      method: 'POST',
      headers: { 'content-type': 'text/plain' }, // (a simple request: no preflight)
      body: JSON.stringify({ ...C.auth, k: w.k, max: w.max, cond: w.cond, remove: !!w.remove }),
    }).then((r) => { if (!r.ok) throw new Error('save ' + r.status); return r.json(); });
  }
  function signInFor(w) {
    store.set([...store.get().filter((x) => x.k !== w.k), w]);
    // back to this exact page (search terms included), carrying the card, so it's saved even if sign-in finishes
    // in another browser (phones) and this one's localStorage never sees it
    const p = new URLSearchParams(location.search);
    ['pw_alert', 'pw_cond', 'pw_save'].forEach((x) => p.delete(x));
    p.set('pw_alert', w.k); p.set('pw_cond', w.cond); p.set('pw_save', '1');
    location.href = C.login.split('?')[0] + '?return_to=' + encodeURIComponent(location.pathname + '?' + p.toString());
  }
  // back from signing in: save whatever was waiting
  function flushPending(skip) {
    if (!C.auth) return;
    const list = store.get().filter((w) => w.k !== skip); store.set(list); if (!list.length) return;
    store.set([]);
    Promise.all(list.map((w) => post(w).then(() => w).catch(() => null))).then((done) => {
      const ok = done.filter(Boolean);
      if (ok.length) toast(`<div class="pw-toast__main"><div class="pw-toast__text"><span class="pw-eyebrow">Alert set</span><strong>${esc(ok.length === 1 ? ok[0].name : ok.length + ' cards')}</strong><span class="pw-muted">We'll email you when we list ${ok.length === 1 ? 'one' : 'them'}.</span></div><a class="pw-btn pw-btn--sm pw-btn--ghost" href="${C.collect}#want">Want list</a></div>`, 6000);
      const failed = list.filter((w) => !ok.includes(w));
      if (failed.length) store.set(failed);
    });
  }

  /* ------------------------------------------------------------------ the sheet */
  function close() { document.querySelector('.pww-layer')?.remove(); document.documentElement.classList.remove('pww-lock'); }
  function open(card, opts = {}) {
    close();
    const el = document.createElement('div');
    el.className = 'pww-layer';
    el.innerHTML = `<div class="pww-scrim" data-pww-close></div>
      <div class="pww-sheet" role="dialog" aria-modal="true" aria-labelledby="pwwTitle">
        <button class="pww-x" type="button" data-pww-close aria-label="Close">\u00d7</button>
        <div class="pww-card">${art(card, 'pww-art--lg')}
          <div><p class="pw-eyebrow">Stock alert</p><h2 id="pwwTitle" class="pww-h">${esc(card.name)}</h2><p class="pww-sub">${line(card)}</p><p class="pww-stock">${stockLine(card)}</p></div></div>
        <form class="pww-form" novalidate>
          <label class="pww-field"><span>Condition</span>
            <select name="cond"><option value="NM">Near mint only</option><option value="LP">Lightly played or better</option><option value="PO">Any condition</option></select></label>
          <button class="pw-btn pww-go" type="submit">${C.auth ? 'Alert me' : 'Create a free account to get the alert'}</button>
          <p class="pww-note">${C.auth ? `We'll email <b>${esc(C.auth.email)}</b>. Change or remove it any time in your <a href="${C.collect}#want">want list</a>.` : 'Just your email, no password: we send you a code. Already have an account? It signs you in instead. Your alert is set as soon as you\'re in.'}</p>
        </form>
      </div>`;
    document.body.appendChild(el);
    document.documentElement.classList.add('pww-lock');
    el.addEventListener('click', (e) => { if (e.target.closest('[data-pww-close]')) close(); });
    const onKey = (e) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); } };
    document.addEventListener('keydown', onKey);
    const form = el.querySelector('form');
    if (opts.cond && form.cond.querySelector(`option[value="${opts.cond}"]`)) form.cond.value = opts.cond;
    form.querySelector('.pww-go').focus({ preventScroll: true });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const w = { k: card.k, name: card.name, cond: form.cond.value }; // (no price: we email for any copy we list)
      if (!C.auth) return signInFor(w);
      const b = form.querySelector('.pww-go'); b.disabled = true; b.textContent = 'Saving\u2026';
      post(w).then((res) => {
        el.querySelector('.pww-sheet').innerHTML = `<button class="pww-x" type="button" data-pww-close aria-label="Close">\u00d7</button>
          <div class="pww-done"><div class="pww-tick" aria-hidden="true"></div><h2 class="pww-h">Alert set for ${esc(card.name)}</h2>
          <p>${res && res.emailed ? `We've emailed <b>${esc(C.auth.email)}</b> to confirm. We'll email you again the moment we list one.` : `We'll email <b>${esc(C.auth.email)}</b> the moment we list one.`}</p>
          <div class="pww-row"><a class="pw-btn pw-btn--ghost" href="${C.collect}#want">See your want list</a><button class="pw-btn" type="button" data-pww-close>Done</button></div></div>`;
        document.dispatchEvent(new CustomEvent('pw:want-added', { detail: w }));
      }).catch(() => { b.disabled = false; b.textContent = 'Alert me'; toast('<div class="pw-toast__main"><div class="pw-toast__text"><strong>Couldn\'t save that</strong><span class="pw-muted">Check your connection and try again.</span></div></div>'); });
    });
    // back from signing in / creating an account: save it without another tap
    if (opts.autosave && C.auth) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  /* ------------------------------------------------------------------ lists of cards */
  function tiles(cards, compact) {
    return cards.map((c) => `<div class="pww-tile${compact ? ' pww-tile--sm' : ''}">${art(c)}
      <div class="pww-tile__t"><strong>${esc(c.name)}</strong><small>${line(c)}</small>${stockLine(c)}</div>
      <button class="pw-btn pw-btn--sm${c.inStock ? ' pw-btn--ghost' : ''}" type="button" data-pww-k="${esc(c.k)}">Alert me</button></div>`).join('');
  }
  const byKey = new Map();
  const remember = (cards) => cards.forEach((c) => byKey.set(c.k, c));
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-pww-k]');
    if (!b) return;
    e.preventDefault();
    const c = byKey.get(b.dataset.pwwK);
    if (c) open(c);
  });

  // the search page, when the shop has nothing for the search
  function fillSearch(box) {
    const q = box.dataset.q, more = box.dataset.mode === 'more';
    search(q, more ? 12 : 8).then((all) => {
      // under results: only the cards we don't have (the ones we do are already in the grid above)
      const cards = more ? all.filter((c) => !c.inStock).slice(0, 6) : all;
      if (!cards.length) return;
      remember(cards);
      box.innerHTML = more
        ? `<div class="pww-search__head"><h2 class="pw-h3">Not the one you're after?</h2><p>These aren't in stock right now. Tap <b>Alert me</b> and we'll email you the moment we list one.</p></div><div class="pww-grid">${tiles(cards)}</div>`
        : `<div class="pww-search__head"><h2 class="pw-h3">We don't have it right now</h2><p>Tap <b>Alert me</b> on the card you're after and we'll email you the moment we list one.</p></div><div class="pww-grid">${tiles(cards)}</div>`;
      box.hidden = false;
      if (location.hash === '#pw-alerts' && box.id === 'pw-alerts') box.scrollIntoView({ block: 'start' });
    });
  }
  // the dropdown under the search box, when there are no listings
  function fillSuggest(el, q) {
    return search(q, 3).then((cards) => {
      if (!cards.length || !el.isConnected) return false;
      remember(cards);
      el.innerHTML = `<div class="pww-suggest__h">We don't have it right now. Get an alert:</div>${tiles(cards, true)}`;
      return true;
    });
  }

  // ?pw_alert=TWM-130 (staff QR at shows): open that card's sheet straight away
  // ?pw_alert=...&pw_save=1 (back from signing in): save it straight away
  function fromUrl() {
    const p = new URLSearchParams(location.search), k = (p.get('pw_alert') || '').toUpperCase();
    if (!/^[A-Z0-9.]+-[A-Z0-9]+$/.test(k)) return '';
    const save = p.get('pw_save') === '1', cond = (p.get('pw_cond') || '').toUpperCase();
    if (save) { // (a refresh shouldn't save it again)
      ['pw_save', 'pw_cond', 'pw_alert'].forEach((x) => p.delete(x));
      const q = p.toString(); history.replaceState(null, '', location.pathname + (q ? '?' + q : '') + location.hash);
    }
    const [code, n] = [k.slice(0, k.lastIndexOf('-')), k.slice(k.lastIndexOf('-') + 1)];
    search(`${code} ${n}`, 12).then((cards) => {
      const c = cards.find((x) => x.k === k);
      if (c) { remember([c]); open(c, { cond, autosave: save }); }
    });
    return save ? k : '';
  }

  window.PWWant = { search, open, fillSearch, fillSuggest };
  const start = () => {
    document.querySelectorAll('[data-pw-want-search]').forEach(fillSearch);
    flushPending(fromUrl()); // (the card in the link is saved by its own sheet, not twice)
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();

  /* ------------------------------------------------------------------ styles (brand tokens from pwc.css) */
  const css = document.createElement('style');
  css.textContent = `
  .pww-lock{overflow:hidden}
  .pww-layer,.pww-layer *{box-sizing:border-box}.pww-layer{position:fixed;inset:0;z-index:2147483000;display:grid;place-items:end center}
  @media(min-width:700px){.pww-layer{place-items:center}}
  .pww-scrim{position:absolute;inset:0;background:rgba(14,45,52,.45)}
  .pww-sheet{box-sizing:border-box;position:relative;width:min(460px,100%);max-height:92dvh;overflow:auto;background:var(--pw-paper,#fff);color:var(--pw-ink,#0e2d34);border-radius:20px 20px 0 0;padding:22px 20px calc(20px + env(safe-area-inset-bottom,0px));box-shadow:0 -10px 40px rgba(14,45,52,.2)}
  @media(min-width:700px){.pww-sheet{border-radius:var(--pw-r-lg,20px);padding:26px}}
  .pww-x{position:absolute;top:10px;right:10px;width:40px;height:40px;border:0;border-radius:50%;background:var(--pw-sky,#eef4f7);font-size:22px;line-height:1;color:inherit;cursor:pointer}
  .pww-card{display:grid;grid-template-columns:96px minmax(0,1fr);gap:16px;align-items:center;margin-bottom:16px;padding-right:36px}
  .pww-h{font-size:20px;font-weight:800;margin:2px 0 4px;line-height:1.2}
  .pww-sub{margin:0;font-size:14px;color:var(--pw-muted,#5a7279)}
  .pww-stock{margin:6px 0 0;font-size:13px;font-weight:700}
  .pww-out{color:var(--pw-muted,#5a7279)}.pww-in{color:var(--pw-teal,#164a54)}
  .pww-mono{font-family:"IBM Plex Mono",ui-monospace,monospace;font-weight:500}
  .pww-art{display:block;width:100%;aspect-ratio:63/88;border-radius:6%/4.3%;background-color:var(--pw-sky,#eef4f7);background-repeat:no-repeat;box-shadow:0 2px 8px rgba(14,45,52,.15)}
  .pww-art--none{display:grid;place-items:center;padding:6px;text-align:center;font-size:11px;font-weight:700;color:var(--pw-muted,#5a7279)}
  .pww-form{display:grid;gap:12px}
  .pww-field{display:grid;gap:6px;font-size:14px;font-weight:700}
  .pww-field select,.pww-money{min-height:48px;border:1px solid var(--pw-line-2,rgba(22,74,84,.2));border-radius:12px;background:#fff;font:inherit;font-size:16px;padding:0 12px;color:inherit}
  .pww-money{display:flex;align-items:center;gap:6px}.pww-money input{flex:1;min-width:0;border:0;outline:0;font:inherit;font-size:18px;font-weight:700;background:none;color:inherit}
  .pww-money:focus-within{outline:2px solid var(--pw-teal,#164a54);outline-offset:1px}
  .pww-note{margin:0;font-size:13px;line-height:1.45;color:var(--pw-muted,#5a7279)}
  .pww-go{min-height:50px;font-size:16px}
  .pww-done{text-align:center;display:grid;gap:10px;justify-items:center;padding:8px 4px}
  .pww-done p{margin:0;line-height:1.5}
  .pww-tick{width:56px;height:56px;border-radius:50%;background:var(--pw-teal,#164a54);position:relative}
  .pww-tick::after{content:"";position:absolute;left:19px;top:14px;width:14px;height:24px;border:solid #fff;border-width:0 4px 4px 0;transform:rotate(45deg)}
  .pww-row{display:flex;gap:10px;flex-wrap:wrap;justify-content:center;margin-top:6px}
  .pww-search{margin:28px 0 0}
  .pww-search__head p{margin:4px 0 14px;color:var(--pw-muted,#5a7279)}
  .pww-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:12px}
  .pww-tile{display:grid;grid-template-columns:56px minmax(0,1fr) auto;gap:12px;align-items:center;padding:10px;border:1px solid var(--pw-line,rgba(22,74,84,.12));border-radius:var(--pw-r,14px);background:#fff}
  .pww-tile__t{display:grid;gap:2px;min-width:0}
  .pww-tile__t strong{font-size:15px;line-height:1.25;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .pww-tile__t small{font-size:12.5px;color:var(--pw-muted,#5a7279);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .pww-tile__t .pww-out,.pww-tile__t .pww-in{font-size:12px;font-weight:700}
  .pww-tile--sm{grid-template-columns:40px minmax(0,1fr) auto;padding:8px 10px;border:0;border-radius:10px;background:none}
  .pww-suggest__h{padding:10px 12px 2px;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--pw-muted,#5a7279)}
  .pww-search-hint{margin:14px 0 0;padding:14px 16px;border-radius:var(--pw-r,14px);background:var(--pw-sky,#eef4f7);font-size:15px;line-height:1.45}
  `;
  document.head.appendChild(css);
})();
